import { Box, CircularProgress, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { ThirdPartyIntegrationService } from '../../../service/ThirdPartyIntegrationService';

export const INTEGRATION_OAUTH_DONE = 'integration-oauth-done';

let oauthCallbackStarted = false;

const notifyOpener = (payload: {
  type: string;
  success: boolean;
  provider: string;
  error?: string;
}) => {
  if (typeof window !== 'undefined' && window.opener) {
    window.opener.postMessage(payload, window.location.origin);
  }
};

const ThirdPartyOAuthCallback: React.FC = () => {
  const { provider } = useParams<{ provider: string }>();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(true);

  useEffect(() => {
    if (!provider || oauthCallbackStarted) return;
    oauthCallbackStarted = true;

    const code = searchParams.get('code');
    const errorParam = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    if (errorParam) {
      const errMsg = errorDescription || errorParam;
      setError(errMsg);
      setSending(false);
      oauthCallbackStarted = false;
      ThirdPartyIntegrationService.oauthDeny(
        provider,
        searchParams.get('state') ?? undefined,
      ).catch(() => {});
      notifyOpener({
        type: INTEGRATION_OAUTH_DONE,
        success: false,
        provider,
        error: errMsg,
      });
      setTimeout(() => window.close(), 600);
      return;
    }

    if (!code) {
      setError('Missing authorization code');
      setSending(false);
      oauthCallbackStarted = false;
      notifyOpener({
        type: INTEGRATION_OAUTH_DONE,
        success: false,
        provider,
        error: 'Missing authorization code',
      });
      setTimeout(() => window.close(), 600);
      return;
    }

    const run = async () => {
      try {
        await ThirdPartyIntegrationService.connect(provider, {
          mode: 'oauth',
          code,
        });
        notifyOpener({ type: INTEGRATION_OAUTH_DONE, success: true, provider });
        window.close();
      } catch {
        setError('Could not complete sign in. Please try again.');
        setSending(false);
        oauthCallbackStarted = false;
        notifyOpener({
          type: INTEGRATION_OAUTH_DONE,
          success: false,
          provider,
          error: 'Could not complete sign in. Please try again.',
        });
      }
    };

    run();
  }, [provider, searchParams]);

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px',
          gap: 2,
          p: 2,
        }}
      >
        <Typography color="error">{error}</Typography>
        <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
          Closing window...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '200px',
        gap: 2,
      }}
    >
      <CircularProgress />
      <Typography color="text.secondary">
        {sending ? 'Completing sign in...' : `Signing in with ${provider}...`}
      </Typography>
    </Box>
  );
};

export default ThirdPartyOAuthCallback;
